<template>
    <div id="root">
        <b-row>
            <b-col cols="4">
                <BaseTextOutput label="Name:" :value="certificate.name"/>
            </b-col>
            <b-col cols="4">
                <BaseTextOutput label="Certificate Status:" :value="certificate.status"/>
            </b-col>
        </b-row>
        <b-row>
            <b-col cols="4">
                <BaseTextOutput label="Certificate Number:" :value="certificate.number"/>
            </b-col>
            <b-col cols="4">
                <BaseTextOutput label="Issue Date:" :value="formatDate(certificate.approvedDate)"/>
            </b-col>
            <b-col cols="4">
                <BaseTextOutput label="Expiration Date:" :value="formatDate(certificate.expireDate)"/>
            </b-col>
        </b-row>
        <b-row>
            <b-col cols="4">
                <BaseTextOutput label="Renewal Status:" :value="certificate.renewalStatus"/>
            </b-col>
            <b-col cols="4">
                <BaseTextOutput label="Processing Timeline:" :value="hasProcessDate ? formatDate(certificate.dueDate) : 'N/A'"/>
            </b-col>
        </b-row>
      <b-row>
        <b-col>
          <div class="text-danger" v-if="hasComplaints">You have a substantiated finding of abuse, neglect and/or misappropriation of property. For information you may call the Registry at (651) 215-8705.</div>
        </b-col>
      </b-row>

        <b-row class="mt-3">
            <b-col cols="4">
                <b-button @click="showEmploymentHistoryModal" variant="secondary" class="mr-1 mt-1">
                    View Employment History
                </b-button>
            </b-col>
            <b-col cols="4">
                <b-button @click="showTestingHistoryModal" variant="secondary" class="mr-1 mt-1">
                    View Testing History
                </b-button>
            </b-col>
            <b-col cols="4">
                <b-link href="https://www.health.state.mn.us/facilities/providers/nursingassistant/index.html" target="_blank" class="btn btn-secondary">
                    Further Registry Information
                </b-link>
            </b-col>
        </b-row>
        <b-row class="mt-2">
            <b-col cols="4" v-if="hasComplaints != null && !hasComplaints">
                <PrintCertificateButton></PrintCertificateButton>
            </b-col>
            <b-col cols="4">
                <span id="renewalButton">
                    <b-button class="btn btn-primary" variant="primary" @click="renewalDateCheck()" :disabled="hasPendingApplication() || over90DaysToExp()">
                        Renewal Application
                    </b-button>
                </span>
                <b-tooltip target="renewalButton" v-if="hasPendingApplication()">You already have a pending application.</b-tooltip>
                <b-tooltip target="renewalButton" v-if="over90DaysToExp">Renewal applications are accessible ninety (90) days before the expiration date of the nurse aide certificate.</b-tooltip>
            </b-col>
          <b-col cols="4">
            <span id="interstateButton">
              <b-button variant="primary" class="mt-2" href="/register/endorsement" :disabled="hasPendingApplication() || over60DaysToExp()">Submit Interstate Application</b-button>
            </span>
            <b-tooltip target="interstateButton" v-if="hasPendingApplication()">You already have a pending application.</b-tooltip>
            <b-tooltip target="interstateButton" v-if="over60DaysToExp()"> Applications for Interstate Endorsement are accessible sixty (60) days before the expiration date of the nurse aide certificate.</b-tooltip>
             </b-col>
        </b-row>

        <b-modal size="xl" id="employment-history-modal" hide-header hide-footer>
            <h2>Your employment history</h2>
            <EmploymentHistoryTableList slug="employmentHistory" :items="certificate.employerHistory"/>
            <b-button @click="hideEmploymentHistoryModal" style="margin-right:15px;" variant="secondary">Close</b-button>
        </b-modal>

        <b-modal size="xl" id="testing-history-modal" hide-header hide-footer>
            <h2>Your testing history</h2>
            <TestingHistoryTableList slug="testingHistory" :items="certificate.testingHistory"/>
            <b-button @click="hideTestingHistoryModal" style="margin-right:15px;" variant="secondary">Close</b-button>
        </b-modal>

        <b-modal  id="renewal-period-modal" hide-header hide-footer>
        <p>You are not in your renewal period. Please wait until 90 days from your expiration date to renew. </p>
        <b-button @click="hideRenewalCheckModal" style="margin-right:15px;" variant="secondary">Close</b-button>
        </b-modal>
    </div>
</template>

<script>
import axios from "axios"
import axiosRetry from 'axios-retry'
import EmploymentHistoryTableList from "@/components/templates/display/EmploymentHistoryTableList";
import TestingHistoryTableList from "@/components/templates/display/TestingHistoryTableList";
import PrintCertificateButton from "@/components/templates/display/PrintCertificateButton";
import BaseTextOutput from "@/components/templates/input/BaseTextOutput";
import moment from 'moment';

axiosRetry(axios, { retries: 3,
    retryDelay: axiosRetry.exponentialDelay  })

export default {
    props: {
        certificate: [Object]
    },
    data: () => ({
        hasComplaints: null
    }),

    components: {
        EmploymentHistoryTableList,
        TestingHistoryTableList,
        PrintCertificateButton,
        BaseTextOutput
    },

    methods: {

        showEmploymentHistoryModal(){
            this.$bvModal.show('employment-history-modal')
        },
        hideEmploymentHistoryModal(){
            this.$bvModal.hide('employment-history-modal')
        },
        showTestingHistoryModal(){
            this.$bvModal.show('testing-history-modal')
        },
        hideTestingHistoryModal(){
            this.$bvModal.hide('testing-history-modal')
        },
        formatDate(unformattedDate){
            let formattedDate = moment(unformattedDate);
            if(!formattedDate.isValid()) return '';
            return formattedDate.format("MM/DD/YYYY")
        },
        loadComplaints() {
            axios.get(`${process.env.VUE_APP_API_BASE_URL}/certificate/complaints`)
                .then(response => {
                    this.hasComplaints = response.data
                })
        },
        hasPendingApplication(){
            return this.certificate.renewalStatus === 'Received' || this.certificate.renewalStatus === 'Reopened' || this.certificate.renewalStatus === 'Resubmitted';
        },
        over90DaysToExp(){
           return moment(this.certificate.expireDate).diff(moment(),'days',true) > 90;
        },
        over60DaysToExp(){
          return moment(this.certificate.expireDate).diff(moment(), 'days', true) > 60;
        },
        renewalDateCheck(){
          if(moment(this.certificate.expireDate, 'YYYY-MM-DD').subtract(90, 'days').startOf('day') <= moment())
            this.$router.push('/renew');
          else
            this.$bvModal.show('renewal-period-modal')
        },
        hideRenewalCheckModal(){
        this.$bvModal.hide('renewal-period-modal')
        }
    },
    computed: {
        hasProcessDate() {
            var statuses = ["Received","In Progress","Reopened","Resubmitted"]
            return statuses.indexOf(this.certificate?.renewalStatus) > 0
        },
        isActive(){
            return this.certificate?.status === 'ACTIVE'
        }
    },
    created() {
        this.loadComplaints();
    }
}
</script>